import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';

const LoadingSpinner = () => (
  <div className="flex justify-center items-center py-12">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2C2C90]"></div>
  </div>
);

const ErrorMessage = ({ message }) => (
  <div className="text-center py-12">
    <div className="text-red-600 text-lg mb-4">Error loading blog post</div>
    <p className="text-gray-600">{message}</p>
  </div>
);

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPost = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);
        const data = await apiService.getBlogDetail(id);
        // Transform API response to component shape
        const transformed = {
          id: data.id,
          title: data.title,
          image: data.image,
          author: data.author,
          authorImage: data.author_image,
          authorBio: data.author_bio,
          excerpt: data.excerpt,
          tags: Array.isArray(data.tags) ? data.tags : [],
          publishedDate: data.published_at,
          sections: Array.isArray(data.content) ? data.content : []
        };
        setPost(transformed);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [id]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !post) {
    return <ErrorMessage message={error || 'Blog post not found'} />;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      {/* Hero Image */}
      <div className="mb-6">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-[220px] sm:h-[280px] md:h-[340px] object-cover rounded-xl shadow"
        />
      </div>

      {/* Meta, Title, Excerpt */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
            {post.tags && post.tags.length > 0 ? post.tags[0] : 'Healthcare'}
          </span>
          <div className="flex items-center gap-2 text-gray-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10m2 9H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v11a2 2 0 01-2 2z" />
            </svg>
            <span>{new Date(post.publishedDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>
        </div>
        <h1 className="mt-3 text-3xl sm:text-4xl font-bold text-gray-900">{post.title}</h1>
        {post.excerpt && (
          <p className="mt-3 text-gray-700 leading-relaxed">
            {post.excerpt}
          </p>
        )}
      </div>

      {/* Author card */}
      <div className="mb-8 border-t pt-6">
        <div className="flex items-start gap-3">
          <img src={post.authorImage} alt={post.author} className="w-10 h-10 rounded-full object-cover" />
          <div>
            <div className="font-semibold text-gray-800">{post.author}</div>
            <div className="text-sm text-gray-600">{post.authorBio}</div>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="space-y-10">
        {post.sections && post.sections.length > 0 && post.sections.map((sec, idx) => (
          <section key={idx}>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">{sec.heading}</h2>
            <p className="text-gray-700 leading-relaxed mb-4">{sec.description}</p>
            {Array.isArray(sec.sub_sections) && sec.sub_sections.length > 0 && (
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                {sec.sub_sections.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      {/* Back to Blog */}
      <div className="mt-12 pt-6 border-t text-center">
        <button
          onClick={() => navigate('/blog')}
          className="inline-block px-6 py-2 bg-blue-800 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          ← Back to Blog
        </button>
      </div>
    </div>
  );
};

export default BlogPost; 